/**
 * Bacco Café — custom elements for behaviour Dawn does not already provide.
 *
 * Everything else in the redesign POC's main.js is deliberately dropped: the
 * cart/nav drawers, quantity stepper, variant picker, announcement rotator,
 * sticky header and scroll reveals are all native to this theme, and the
 * native versions have focus trapping, keyboard support and inventory clamping
 * the prototype lacked.
 *
 * These are custom elements rather than DOMContentLoaded handlers so they
 * re-initialise automatically when Shopify's Section Rendering API swaps markup.
 */

/**
 * <bacco-form-toggle> — switches the vitrine between whole bean and ground.
 *
 * Both product grids are rendered server-side from their own collection, so
 * switching is instant and needs no request. Hiding is done with a class rather
 * than inline display so CSS keeps ownership of layout.
 */
if (!customElements.get('bacco-form-toggle')) {
  customElements.define(
    'bacco-form-toggle',
    class BaccoFormToggle extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-form-value]'));
        if (!this.buttons.length) return;

        // Panels live outside this element, scoped to the owning section.
        const scope = this.closest('[data-bacco-vitrine]') || document;
        this.panels = Array.from(scope.querySelectorAll('[data-form-panel]'));

        this.buttons.forEach((button) => {
          button.addEventListener('click', () => this.select(button.dataset.formValue));
        });
      }

      select(value) {
        this.buttons.forEach((button) => {
          const isActive = button.dataset.formValue === value;
          button.classList.toggle('active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          button.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.formPanel === value;
          panel.classList.toggle('is-hidden', !isActive);
          panel.toggleAttribute('hidden', !isActive);
        });
      }
    }
  );
}

/**
 * <bacco-gallery> — thumbnail-driven main image on the product page.
 *
 * A deliberately small stand-in for Dawn's <media-gallery>: the coffee products
 * each have a single variant, so there is no variant-to-media linkage to lose,
 * and the POC gallery is a plain image swap. Swaps srcset alongside src so the
 * responsive sources stay correct, and carries the alt text across so the main
 * image never describes the wrong photo.
 */
if (!customElements.get('bacco-gallery')) {
  customElements.define(
    'bacco-gallery',
    class BaccoGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-gallery-main]');
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        if (!this.main || this.thumbs.length < 2) return;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.show(thumb));
        });
      }

      show(thumb) {
        const image = thumb.querySelector('img');
        if (!image) return;

        this.main.src = image.src;
        if (image.srcset) this.main.srcset = image.srcset;
        this.main.alt = thumb.dataset.galleryAlt || image.alt || '';

        this.thumbs.forEach((item) => {
          const isActive = item === thumb;
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }
    }
  );
}

/**
 * <bacco-to-top> — reveals a back-to-top button past the first viewport.
 *
 * Not provided by Dawn. The scroll listener is genuinely passive here; the
 * POC's helper silently dropped the options argument it was given.
 */
if (!customElements.get('bacco-to-top')) {
  customElements.define(
    'bacco-to-top',
    class BaccoToTop extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        this.button.addEventListener('click', () => {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }

      onScroll() {
        this.button.classList.toggle('show', window.scrollY > 600);
      }
    }
  );
}
